import { forwardRef, useMemo, type SelectHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useEquipmentTypes } from '@/features/equipment-types/queries';
import { EQUIPMENT_TYPE_CATEGORIES } from '@/features/equipment-types/types';
import { cn } from '@/lib/utils';

interface EquipmentTypePickerProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  value: string | null;
  onChange: (typeId: string | null) => void;
  /** Hide inactive types from the list (default: true). */
  hideInactive?: boolean;
  /** Allow the empty option (default: false). */
  allowEmpty?: boolean;
  emptyLabel?: string;
}

/**
 * EquipmentTypePicker — native <select> wired to useEquipmentTypes()
 * and grouped by category via <optgroup>. Each option label combines
 * the type name and the code in monospace-friendly format.
 *
 * The picker is the canonical EquipmentType dropdown used in the
 * Equipment create form (F4.2) and any future module that needs to
 * pick a catalog entry (e.g. maintenance plans in F8).
 */
export const EquipmentTypePicker = forwardRef<
  HTMLSelectElement,
  EquipmentTypePickerProps
>(
  (
    { value, onChange, hideInactive = true, allowEmpty = false, emptyLabel, className, ...rest },
    ref,
  ) => {
    const { t } = useTranslation();
    const { data, isLoading } = useEquipmentTypes();

    const grouped = useMemo(() => {
      const filtered = data
        ? hideInactive
          ? data.filter((d) => d.isActive)
          : data
        : [];
      return EQUIPMENT_TYPE_CATEGORIES.map((category) => ({
        category,
        items: filtered.filter((d) => d.category === category),
      }));
    }, [data, hideInactive]);

    return (
      <select
        ref={ref}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        disabled={isLoading || rest.disabled}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      >
        {allowEmpty && <option value="">{emptyLabel ?? '—'}</option>}
        {grouped.map(({ category, items }) =>
          items.length === 0 ? null : (
            <optgroup
              key={category}
              label={t(`equipmentType.category.${category}`)}
            >
              {items.map((etype) => (
                <option key={etype.id} value={etype.id}>
                  {etype.name} ({etype.code})
                </option>
              ))}
            </optgroup>
          ),
        )}
      </select>
    );
  },
);
EquipmentTypePicker.displayName = 'EquipmentTypePicker';
